import { ArrowLeft, ChevronRight, MapPin } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef } from "react";

import {
  ProjectDashboardBlocks,
  ProjectDashboardFeaturedContent,
} from "../../../_components/project-dashboard/ProjectDashboardTemplate";
import {
  alegreyaSans,
  fondamento,
  neueHeitz,
} from "../../../global-fonts";
import type { MapProject } from "./map-project";

const MENU_ITEM_CLASS =
  "group flex w-full cursor-pointer items-center gap-3 px-5 py-4 text-left text-[1.05rem] font-medium leading-snug transition-colors hover:bg-burgundy hover:text-sand focus-visible:bg-burgundy focus-visible:text-sand focus-visible:outline-none";
const FOOTER_LINK_CLASS =
  "text-base text-burgundy underline underline-offset-4 transition-colors hover:text-earth focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-burgundy";

export default function ProjectMapLegend({
  projects,
  selectedProjectId,
  selectedSectionId,
  hoveredProjectId,
  hoveredSectionId,
  onSelectProject,
  onSelectSection,
  onBack,
  onBackToSections,
  onHoverProject,
  onHoverSection,
}: {
  projects: MapProject[];
  selectedProjectId: string | null;
  selectedSectionId: string | null;
  hoveredProjectId: string | null;
  hoveredSectionId: string | null;
  onSelectProject: (projectId: string) => void;
  onSelectSection: (sectionId: string) => void;
  onBack: () => void;
  onBackToSections: () => void;
  onHoverProject: (projectId: string | null) => void;
  onHoverSection: (sectionId: string | null) => void;
}) {
  const selectedProject = projects.find(
    (project) => project.id === selectedProjectId,
  );
  const selectedSection = selectedProject?.sections.find(
    (section) => section.id === selectedSectionId,
  );
  const title = selectedSection?.title ?? selectedProject?.title ?? "Projects";
  const isContentlessSection =
    selectedSection !== undefined &&
    (selectedSection.blocks.length === 0 ||
      (selectedSection.blocks.length === 1 &&
        selectedSection.blocks[0].type === "heading" &&
        selectedSection.blocks[0].title === selectedSection.title));
  const legendRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (legendRef.current) {
      legendRef.current.scrollTop = 0;
    }
  }, [selectedProjectId, selectedSectionId]);

  return (
    <aside
      ref={legendRef}
      aria-labelledby="project-map-legend-title"
      className="absolute inset-0 z-10 h-full w-full overflow-x-hidden overflow-y-auto border border-burgundy bg-sand text-burgundy sm:inset-auto sm:top-6 sm:right-6 sm:h-auto sm:max-h-[calc(100%-3rem)] sm:w-[clamp(24rem,35vw,32rem)] sm:shadow-xl"
    >
      <header
        className={`sticky top-0 z-20 flex min-w-0 items-center gap-3 border-b px-5 py-5 ${selectedSection ? "border-burgundy bg-burgundy text-sand" : "border-burgundy bg-sand"}`}
      >
        {selectedProject ? (
          <button
            type="button"
            aria-label={
              selectedSection ? "Back to content list" : "Back to Projects"
            }
            onClick={selectedSection ? onBackToSections : onBack}
            className={`shrink-0 cursor-pointer transition-transform hover:-translate-x-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 ${selectedSection ? "text-sand hover:text-millet focus-visible:outline-sand" : "text-burgundy hover:text-earth focus-visible:outline-burgundy"}`}
          >
            <ArrowLeft aria-hidden="true" className="size-5" />
          </button>
        ) : null}
        <h2
          id="project-map-legend-title"
          className={`${neueHeitz.className} min-w-0 text-2xl ${isContentlessSection ? "break-words whitespace-normal leading-tight" : "truncate leading-none"} ${selectedSection ? "text-sand" : "text-burgundy"}`}
          title={selectedSection ? selectedSection.title : selectedProject?.title}
        >
          {title}
        </h2>
      </header>
      {selectedSection ? (
        <>
          <ProjectDashboardBlocks
            blocks={selectedSection.blocks}
            compact
            headingToOmit={selectedSection.title}
          />
          <ProjectDashboardFeaturedContent
            featuredProducts={selectedSection.featuredProducts}
            compact
          />
          {selectedSection.storyHref ? (
            <footer className="flex justify-end border-t border-burgundy/20 px-5 py-4">
              <Link
                href={selectedSection.storyHref}
                className={`${alegreyaSans.className} ${FOOTER_LINK_CLASS}`}
              >
                View Story →
              </Link>
            </footer>
          ) : null}
        </>
      ) : (
        <>
          <ul>
            {selectedProject
            ? selectedProject.sections.map((section, sectionIndex) => {
              const isHovered = section.id === hoveredSectionId;

              return (
                <li key={section.id} className="border-t border-burgundy">
                  <button
                    type="button"
                    onClick={() => onSelectSection(section.id)}
                    onMouseEnter={() => onHoverSection(section.id)}
                    onMouseLeave={() => onHoverSection(null)}
                    onFocus={() => onHoverSection(section.id)}
                    onBlur={() => onHoverSection(null)}
                    className={`${alegreyaSans.className} ${MENU_ITEM_CLASS} justify-between ${isHovered ? "bg-burgundy text-sand" : "text-earth"}`}
                  >
                    <span className="flex min-w-0 items-baseline gap-3">
                      <span
                        className={`${fondamento.className} shrink-0 text-xs text-clay group-hover:text-millet group-focus-visible:text-millet`}
                      >
                        {String(sectionIndex + 1).padStart(2, "0")}
                      </span>
                      <span>{section.title}</span>
                    </span>
                    <ChevronRight
                      aria-hidden="true"
                      className="size-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5"
                    />
                  </button>
                </li>
              );
            })
          : projects.map((project) => {
          const isHovered = project.id === hoveredProjectId;

          return (
            <li key={project.id} className="border-t border-burgundy">
              <button
                type="button"
                onClick={() => onSelectProject(project.id)}
                onMouseEnter={() => onHoverProject(project.id)}
                onMouseLeave={() => onHoverProject(null)}
                onFocus={() => onHoverProject(project.id)}
                onBlur={() => onHoverProject(null)}
                className={`${alegreyaSans.className} ${MENU_ITEM_CLASS} ${isHovered ? "bg-burgundy text-sand" : "text-earth"}`}
              >
                <MapPin
                  aria-hidden="true"
                  className="size-4 shrink-0 text-burgundy transition-transform group-hover:-translate-y-0.5 group-hover:text-sand group-focus-visible:-translate-y-0.5 group-focus-visible:text-sand"
                />
                <span>{project.title}</span>
              </button>
            </li>
          );
            })}
          </ul>
          {selectedProject ? (
            <footer className="flex justify-end border-t border-burgundy/20 px-5 py-4">
              <Link
                href={selectedProject.href}
                className={`${alegreyaSans.className} ${FOOTER_LINK_CLASS}`}
              >
                Explore Project →
              </Link>
            </footer>
          ) : null}
        </>
      )}
    </aside>
  );
}